import { Controller } from "@hotwired/stimulus"
import { cable } from "../lair/cable.js"
import { readPurse, changedCoins } from "../lair/purse.js"

// The masthead's purse (shared/_masthead). Drawn true by the server, then
// kept true two ways:
//
// - the wire: PurseChannel streams the keeper's own purse on every write to
//   silver or gold, whoever made it — a shift, a bout, a job paying a
//   chapter's reward, a trade settling in the other keeper's window. On
//   subscribe the channel sends the purse as it stands, which catches a
//   payout that landed between the page rendering and the socket coming up.
// - the page: a controller that has just heard a new balance in its own
//   JSON answer says so on the document ("purse:changed"), so the chip moves
//   the instant the purchase does rather than a round-trip later.
//
// Every other purse on the page (the workshop's, the phone HUD's chip) is
// brought along by class, so no two numbers on one screen disagree.
export default class extends Controller {
  static targets = [ "silver", "gold" ]

  connect() {
    this.onChanged = (event) => this.show(readPurse(event.detail))
    document.addEventListener("purse:changed", this.onChanged)

    this.subscription = cable().subscriptions.create("PurseChannel", {
      received: (data) => this.show(readPurse(data))
    })
  }

  disconnect() {
    document.removeEventListener("purse:changed", this.onChanged)
    this.subscription?.unsubscribe()
    this.subscription = null
  }

  shown() {
    const read = (target) => (target ? parseInt(target.textContent, 10) : NaN)
    return {
      silver: this.hasSilverTarget ? read(this.silverTarget) : undefined,
      gold: this.hasGoldTarget ? read(this.goldTarget) : undefined
    }
  }

  show(purse) {
    const changed = changedCoins(this.shown(), purse)
    if (changed.length === 0) return

    for (const coin of changed) {
      const target = coin === "silver" ? this.silverTarget : this.goldTarget
      target.textContent = purse[coin]
      // The page's other purses, by class: the workshop's counts and the
      // phone HUD's chip read these too (shift_controller paints them the
      // same way).
      document.querySelectorAll(`.ll-${coin}-count`).forEach((node) => { node.textContent = purse[coin] })
      this.pulse(target.closest(".ll-purse__coin"), "ll-purse__coin--changed")
    }

    const silver = this.hasSilverTarget ? this.silverTarget.textContent : "?"
    const gold = this.hasGoldTarget ? this.goldTarget.textContent : "?"
    this.element.setAttribute("aria-label", `Purse: ${silver} silver, ${gold} gold`)
    this.pulse(this.element, "ll-purse--tick")
  }

  // Re-run a one-shot animation by removing and re-adding its class on the
  // next frame; two changes in a row each get their tick.
  pulse(node, className) {
    if (!node) return
    node.classList.remove(className)
    requestAnimationFrame(() => node.classList.add(className))
  }
}
